import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';

import CoreGenerateIcon from '@webclient/components/generate-icon';
import Footer from '@webclient/components/workspace/setup/components/footer';
import Header from '@webclient/components/workspace/setup/components/header';
import useWorkspaceSetup from '@webclient/components/workspace/setup/hooks/useWorkspaceSetup';
import ImagePreview from '@webclient/components/workspace/setup/sections/steps/components/image-preview';

import { queryClient } from '@webclient/providers/ReactQueryProvider';

import useQueryParams from '@webclient/hooks/useQueryParams';
import useUploadByLinks from '@webclient/hooks/useUploadByLinks';

import { useTranslate } from '@tolgee/react';

type CachedType = {
  links: Array<{
    url: string;
  }>;
};

export default function GenerateIcon() {
  const {
    generatedIconsDescription,
    handleSetGeneratedIcon,
    handleSetGeneratedIconDescription,
    selectedIcon,
    handleGoToStep,
    handleGoToPreviousStep,
    uploadedAvatar,
    generatedIcons,
    handleSetSelectedIcon,
    handleReset,
    handleSetUploadedAvatar,
    handleSelectedUploadOption,
  } = useWorkspaceSetup();

  const { removeQueryParamByKey } = useQueryParams();
  const { t } = useTranslate();
  function handleCloseModal() {
    removeQueryParamByKey('action');
    handleReset();
  }

  const {
    mutateAsync: mutateUploadByLinksAsync,
    isPending: isUploadByLinksPending,
  } = useUploadByLinks();

  function handleSkipStep() {
    handleGoToStep('invitation');
  }

  function handleSkipUploadIfIconIsAlreadyUploaded() {
    const uploadedIcons = queryClient
      .getMutationCache()
      .findAll({ mutationKey: ['uploadByLinks'] });

    for (const icon of uploadedIcons) {
      if (
        icon.state.variables &&
        (icon.state.variables as CachedType).links[0].url === selectedIcon
      ) {
        handleGoToStep('cropper');
        return;
      }
    }
  }

  async function handleNext() {
    handleSkipUploadIfIconIsAlreadyUploaded();

    await mutateUploadByLinksAsync({
      context: {
        usage: 'profile_picture',
      },
      handler: 'curl',
      links: [
        {
          url: uploadedAvatar
            ? URL.createObjectURL(uploadedAvatar)
            : selectedIcon,
        },
      ],
    });
    handleGoToStep('cropper');
  }

  return (
    <CoreGenerateIcon
      generateImageParams={{
        quality: 'standard',
        size: '1024x1024',
        rewrite: 'logo',
      }}
      handleSelectedUploadOption={
        handleSelectedUploadOption as (option: string) => void
      }
      handleSetUploadedAvatar={handleSetUploadedAvatar}
      Header={
        <Header
          onClose={handleCloseModal}
          Icon={<ImagePreview />}
          title={t(
            'page.workspace.createWorkspaceModal.step2.title',
            'Customize your Knowledge Base',
          )}
          subtitle={t(
            'page.workspace.createWorkspaceModal.step2.description',
            'Make your Knowledge Base instantly recognizable with a unique icon - use your logo, branding elements, a photo, or let Knowz create one for you.',
          )}
        />
      }
      Footer={
        <Footer
          NextButton={
            <LoadingButton
              onClick={handleNext}
              size="large"
              loading={isUploadByLinksPending}
              disabled={!selectedIcon && !uploadedAvatar} //TODO: if user selects a generated icon or uploads an icon we enable this button
              variant="contained"
            >
              {t('page.workspace.createWorkspaceModal.nextButton', 'Next')}
            </LoadingButton>
          }
          BackButton={
            <Button
              size="large"
              onClick={handleGoToPreviousStep}
              color="secondary"
              variant="outlined"
            >
              {t('page.workspace.createWorkspaceModal.backButton', 'Back')}
            </Button>
          }
          SkipButton={
            <ButtonBase
              disableRipple
              disableTouchRipple
              onClick={handleSkipStep}
            >
              {t('page.workspace.createWorkspaceModal.skipButton', 'Skip step')}
            </ButtonBase>
          }
        />
      }
      generatedIcons={generatedIcons}
      generatedIconsDescription={generatedIconsDescription}
      handleCloseModal={handleCloseModal}
      handleGoToPreviousStep={handleGoToPreviousStep}
      handleGoToStep={handleGoToStep as (step: string) => void}
      handleSetGeneratedIcon={handleSetGeneratedIcon}
      handleSetGeneratedIconDescription={handleSetGeneratedIconDescription}
      handleSetSelectedIcon={handleSetSelectedIcon}
      handleSkipStep={handleSkipStep}
      selectedIcon={selectedIcon}
      uploadedAvatar={uploadedAvatar}
    />
  );
}
